import { Box, Center, Icon, Image } from '@hover/blueprint';
import type { BoxProps } from '@hover/blueprint/chakra';
import { iconImages } from '@hover/icons';
import React from 'react';

import { BottomText } from 'src/modules/homeownerApp/components/BottomText';
import {
  formatSecondaryTextForDisplayImage,
  formatSpaceType,
} from 'src/modules/homeownerApp/components/Utils';
import { useFormattedTimeAgo } from 'src/modules/homeownerApp/hooks';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

interface MaterialSelectionCardProps extends Omit<BoxProps, 'id' | 'onClick'> {
  aiDisplayImage: AiDisplayImage;
  onClick: (aiDisplayImage: AiDisplayImage) => void;
}

export const PROJECT_CARD_HEIGHT = '262px';
export const PROJECT_CARD_WIDTH = '253px';

const IMAGE_HEIGHT = '190px';

export const MaterialSelectionCard = ({
  aiDisplayImage,
  onClick,
  ...props
}: MaterialSelectionCardProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { aiGeneratedImage, createdAt, spaceType } = aiDisplayImage;
  const { generatedImagePath, imagePath } = aiGeneratedImage;
  const imageSrc = generatedImagePath || imagePath;
  const dateCreated = useFormattedTimeAgo(createdAt);

  const secondaryText = formatSecondaryTextForDisplayImage(
    t('generatedImageThumbnail.customized'),
    dateCreated,
    aiGeneratedImage,
  );

  return (
    <Box
      alignItems="flex-start"
      borderRadius={400}
      cursor="pointer"
      flexDir="column"
      gap={0}
      height={PROJECT_CARD_HEIGHT}
      minW={PROJECT_CARD_WIDTH}
      onClick={() => onClick(aiDisplayImage)}
      overflow="hidden"
      shadow={{ base: 'distance300', tablet: 'distance400' }}
      width={PROJECT_CARD_WIDTH}
      {...props}
    >
      {imageSrc ? (
        <Image
          alt={`image-${aiGeneratedImage.id}`}
          height={IMAGE_HEIGHT}
          minH={IMAGE_HEIGHT}
          objectFit="cover"
          src={imageSrc}
          transition="filter 0.2s ease-in-out"
          width="100%"
        />
      ) : (
        <Center
          backgroundColor="neutral.50"
          height={IMAGE_HEIGHT}
          minH={IMAGE_HEIGHT}
          width="100%"
        >
          <Icon color="neutral.500" icon={iconImages} size="medium" />
        </Center>
      )}
      <Box alignItems="center" gap={200} h="100%" px="400">
        <BottomText
          primaryText={formatSpaceType(spaceType as string)}
          secondaryText={secondaryText}
        />
      </Box>
    </Box>
  );
};
